import { guardianPlayerKeys } from '@/features/guardian-player/queries/query-keys';
import {
  fetchGuardianPlayerSessionDetail,
  fetchGuardianPlayerSessionSummary,
  fetchGuardianPlayerSessions,
  type GuardianPlayerSessionsParams,
} from '@/features/guardian-player/services';
import { AuthServices } from '@/features/auth/services';
import { buildGuardianPlayerSessionDetail } from '@/features/guardian-player/utils';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

const DEFAULT_SESSION_HISTORY_LIMIT = 10;

export function useGuardianPlayerSessionSummaryQuery(playerId: string) {
  return useQuery({
    queryKey: guardianPlayerKeys.sessionSummary(playerId),
    queryFn: () => fetchGuardianPlayerSessionSummary(playerId),
  });
}

export function useGuardianPlayerDetailQuery(playerId: string) {
  return useQuery({
    queryKey: guardianPlayerKeys.playerDetail(playerId),
    queryFn: () => AuthServices.getGuardianPlayerDetail(playerId),
    enabled: Boolean(playerId),
  });
}

export function useGuardianPlayerSessionsQuery(
  playerId: string,
  params: GuardianPlayerSessionsParams = {},
) {
  const limit = params.limit ?? DEFAULT_SESSION_HISTORY_LIMIT;
  const paginate = params.paginate ?? true;
  const initialCursor = params.cursor;

  return useInfiniteQuery({
    queryKey: guardianPlayerKeys.sessions(playerId, {
      limit,
      cursor: initialCursor,
      paginate,
    }),
    queryFn: ({ pageParam }) =>
      fetchGuardianPlayerSessions(playerId, {
        limit,
        paginate,
        cursor: pageParam || undefined,
      }),
    initialPageParam: initialCursor,
    getNextPageParam: lastPage => lastPage.nextCursor || undefined,
  });
}

export function useGuardianPlayerSessionDetailQuery(playerId: string, sessionId: string) {
  return useQuery({
    queryKey: guardianPlayerKeys.sessionDetail(playerId, sessionId),
    queryFn: () => fetchGuardianPlayerSessionDetail(playerId, sessionId),
    enabled: !!playerId && !!sessionId,
    select: buildGuardianPlayerSessionDetail,
  });
}
